import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Radio, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Slim red banner that appears on any page ONLY when a broadcast is
 * currently live. Polls /api/live/status every 30s.
 *
 * Currently mounted at the top of /editors-letter so a member reading the
 * weekly letter is gently nudged into the live salon without leaving the
 * page.
 *
 * Renders nothing when no broadcast is active — never blocks content.
 */
export const LiveNowBanner = () => {
  const [live, setLive] = useState(null);

  useEffect(() => {
    let active = true;
    const refresh = async () => {
      try {
        const { data } = await api.get("/live/status");
        if (!active) return;
        setLive(data?.active || null);
      } catch {
        if (active) setLive(null);
      }
    };
    refresh();
    const t = setInterval(refresh, 30000);
    return () => { active = false; clearInterval(t); };
  }, []);

  if (!live || !live.id) return null;

  return (
    <Link
      to={`/broadcast/${live.id}`}
      data-testid="live-now-banner"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 16,
        padding: "10px 24px",
        background: "#FFF1EE",
        borderBottom: "1px solid rgba(211, 63, 63, 0.55)",
        boxShadow: "0 2px 12px rgba(211, 63, 63, 0.12)",
        textDecoration: "none",
        color: "inherit",
        position: "sticky",
        top: 76,
        zIndex: 40,
      }}
    >
      <style>{`
        @keyframes salonLivePulse {
          0%   { box-shadow: 0 0 0 0 rgba(211, 63, 63, 0.65); }
          70%  { box-shadow: 0 0 0 8px rgba(211, 63, 63, 0); }
          100% { box-shadow: 0 0 0 0 rgba(211, 63, 63, 0); }
        }
      `}</style>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 12, minWidth: 0, flex: 1 }}>
        <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
          <Radio size={16} strokeWidth={1.7} color="#D33F3F" />
          <span
            aria-hidden="true"
            style={{
              position: "absolute",
              top: -3,
              right: -4,
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: "#D33F3F",
              boxShadow: "0 0 0 0 rgba(211, 63, 63, 0.7)",
              animation: "salonLivePulse 1.6s ease-out infinite",
            }}
          />
        </span>
        <span
          style={{
            fontSize: 10,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 700,
            color: "#D33F3F",
            flexShrink: 0,
          }}
        >
          Live now
        </span>
        <span
          className="font-serif"
          style={{
            fontSize: 14,
            fontStyle: "italic",
            color: "var(--text-primary)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {live.title || "A broadcast is on right now."}
        </span>
      </span>
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 10.5,
          letterSpacing: "0.22em",
          textTransform: "uppercase",
          fontFamily: "'Outfit', sans-serif",
          fontWeight: 500,
          color: "#D33F3F",
          flexShrink: 0,
        }}
      >
        Watch <ArrowRight size={12} strokeWidth={2} />
      </span>
    </Link>
  );
};

export default LiveNowBanner;
